import ActionButton from 'Components/ActionButton';
import FormRow from 'Components/FormRow';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import { isNumber } from 'neuro-utils';
import * as React from 'react';
import { injectIntl } from 'react-intl';
import { otherOnChange } from '../../utils';
import DosageFormat from '../DosageFormat';
import UpkeepDoses from './UpkeepDoses';

import type { IntlShape } from 'react-intl';

const getStrengthValue = (s: Array<IStrengths> | null | undefined): number | undefined => {
  if (!s || s.length === 0) return undefined;
  const key = Object.keys(s[0] ?? {})[0];
  if (!key) return undefined;
  const value = s[0]?.[key];
  const parsed = parseInt(value ?? '');
  if (!isNumber(parsed)) return undefined;
  return parsed;
};

const ParkinsonInfusion = ({ type, index, formData, intl }: IInfusionProps): React.JSX.Element => {
  const fm = (id: string): string => intl.formatMessage({ id });

  const regimen = formData.document.regimen?.[index] as IRegimenCustom;

  const onChangeDoses = (values: TOnChangeValues): void => {
    otherOnChange(
      formData.onChange,
      index,
      regimen,
      formData.document.regimen ?? undefined,
    )({ infusionDoses: { ...regimen.infusionDoses, ...values } });
  };

  const onChangeDosesMlh = (values: TOnChangeValues): void => {
    const key = Object.keys(values)[0];
    if (!key) return;
    const strengthValue = getStrengthValue(regimen.strengths);
    if (!isNumber(strengthValue)) return;
    if (typeof values[key] === 'number') values[key] *= strengthValue;
    onChangeDoses(values);
  };

  const transformToMlhValue = (mgh: number | null | undefined): number | undefined => {
    const strengthValue = getStrengthValue(regimen.strengths);
    if (!isNumber(strengthValue) || strengthValue === 0) return undefined;
    return mgh ? mgh / strengthValue : undefined;
  };

  const addUpkeepDose = (): void => {
    const doses = regimen.infusionDoses?.upkeepDoses ? [...regimen.infusionDoses.upkeepDoses] : [];
    doses.push({
      upkeepDose: undefined,
      upkeepDoseFrom: undefined,
      upkeepDoseTo: undefined,
    });
    otherOnChange(
      formData.onChange,
      index,
      regimen,
      formData.document.regimen ?? undefined,
    )({ infusionDoses: { ...regimen.infusionDoses, upkeepDoses: doses } });
  };

  return (
    <React.Fragment>
      <FormRow title="medication.regimenDosage" headerWidth={3} description={fm('medication.infusionDosageInfo')}>
        <>
          {type === 'gel' && (
            <DosageFormat
              header={fm('medication.regimenMorningDose')}
              content={
                <Container alignItems="center">
                  <Item xs={2}>
                    <Container>
                      <Item>
                        <InputHandler
                          type="NumberField"
                          editing={true}
                          name="morningDose"
                          formData={{
                            onChange: onChangeDosesMlh,
                            document: {
                              morningDose: transformToMlhValue(regimen.infusionDoses?.morningDose) ?? '',
                            },
                          }}
                          width={7}
                          precision={2}
                        />
                      </Item>
                      <Item style={{ padding: '0 1rem' }}>{fm('medication.regimenInfusion_ml')}</Item>
                    </Container>
                  </Item>
                  <Item xs={1}>=</Item>
                  <Item>
                    <InputHandler
                      type="NumberField"
                      editing={true}
                      name="morningDose"
                      formData={{
                        onChange: onChangeDoses,
                        document: { morningDose: regimen.infusionDoses?.morningDose ?? '' },
                      }}
                      width={7}
                      precision={2}
                    />
                  </Item>
                  <Item style={{ padding: '0 1rem' }}>{fm('medication.regimenInfusion_mg')}</Item>
                </Container>
              }
            />
          )}
          {/** Upkeep dose input table */}
          <UpkeepDoses index={index} formData={formData} str={getStrengthValue(regimen.strengths)} />
          {/** Upkeep dose add button */}
          <Container>
            <Item style={{ marginBottom: '2rem' }}>
              <ActionButton
                text={'medication.doses.addDosage'}
                onClick={addUpkeepDose}
                width={16}
                height={3}
                fontSize={16}
              />
            </Item>
          </Container>
          <DosageFormat
            header={fm('medication.regimenAdditionalDose')}
            content={
              <Container alignItems="center">
                <Item xs={2}>
                  <Container>
                    <Item>
                      <InputHandler
                        type="NumberField"
                        editing={true}
                        name="additionalDose"
                        formData={{
                          onChange: onChangeDosesMlh,
                          document: {
                            additionalDose: transformToMlhValue(regimen.infusionDoses?.additionalDose) ?? '',
                          },
                        }}
                        width={7}
                        precision={2}
                      />
                    </Item>
                    <Item style={{ padding: '0 1rem' }}>{fm('medication.regimenInfusion_ml')}</Item>
                  </Container>
                </Item>
                <Item xs={1}>=</Item>
                <Item xs={2}>
                  <Container>
                    <Item>
                      <InputHandler
                        type="NumberField"
                        editing={true}
                        name="additionalDose"
                        formData={{
                          onChange: onChangeDoses,
                          document: { additionalDose: regimen.infusionDoses?.additionalDose ?? '' },
                        }}
                        width={7}
                        precision={2}
                      />
                    </Item>
                    <Item style={{ padding: '0 1rem' }}>{fm('medication.regimenInfusion_mg')}</Item>
                  </Container>
                </Item>
                <Item>
                  <Container>
                    <Item style={{ padding: '0 0 0 4rem' }}>
                      <InputHandler
                        type="NumberField"
                        editing={true}
                        name="additionalDoseLimit"
                        formData={{
                          onChange: onChangeDoses,
                          document: { additionalDoseLimit: regimen.infusionDoses?.additionalDoseLimit ?? '' },
                        }}
                        width={7}
                        precision={2}
                      />
                    </Item>
                    <Item style={{ padding: '0 1rem' }}>{fm('medication.regimenInfusion_timesPerDay')}</Item>
                  </Container>
                </Item>
              </Container>
            }
          />
        </>
      </FormRow>
    </React.Fragment>
  );
};

interface IInfusionProps {
  type: 'infusion' | 'gel' | false;
  index: number;
  formData: IFormData<IDocumentWithTypeV2<'medication'>>;
  intl: IntlShape;
}

export default injectIntl(ParkinsonInfusion);
